import { formatEventMoney } from "../../utils/eventCurrency";

const PAYMENT_FIELDS = [
  "payment_category_1",
  "payment_category_2",
  "payment_category_3",
  "payment_category_4",
];

const SHARED_COST_CATEGORIES = [
  ["payment_category_1", "Equipment and materials"],
  ["payment_category_2", "Venue rental"],
  ["payment_category_3", "Transport and logistics"],
  ["payment_category_4", "Other shared expense"],
];

function parseAmount(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseFloat(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function getPositiveAmount(value) {
  const amount = parseAmount(value);
  return amount && amount > 0 ? amount : null;
}

function getSharedCostTotal(event) {
  const total = PAYMENT_FIELDS.reduce((sum, field) => {
    const amount = getPositiveAmount(event?.[field]);
    return amount ? sum + amount : sum;
  }, 0);
  return total > 0 ? total : null;
}

function getSharedCostItems(event, t, lang) {
  return SHARED_COST_CATEGORIES.flatMap(([field, category]) => {
    const amount = getPositiveAmount(event?.[field]);
    if (!amount) return [];
    return [{
      label: t(`event_create.step3.payment.categories.${category}`, {
        defaultValue: category,
      }),
      value: formatEventMoney(amount, event, lang),
    }];
  });
}

function getPrimaryTicketAmount(event) {
  const preview = event?.ticket_purchase_preview || event?.participant_ticketing || {};
  const candidates = [
    preview.full_amount,
    preview.total_amount,
    preview.join_checkout_amount,
    event?.ticket_price,
    event?.price,
    event?.payment_category_1,
  ];

  for (const candidate of candidates) {
    const amount = getPositiveAmount(candidate);
    if (amount) return amount;
  }

  return null;
}

export function isEventTicketPresentation(event) {
  const pricingModel = String(event?.pricing_model || "").toLowerCase();
  if (pricingModel === "host_paid") return true;

  const feeType = Number(event?.fee_type ?? 0);
  const hasPaidAmount = Boolean(getSharedCostTotal(event) || getPrimaryTicketAmount(event));
  if (feeType !== 1 && !hasPaidAmount) return false;

  const ticketingFlowKind = String(event?.ticketing_flow_kind || "").toLowerCase();
  // Checkout flags describe when payment collection is active, not whether
  // the event uses tickets. VaultGrid-team Revolut events are created with
  // is_platform_event and must keep their ticket presentation while the host
  // is still recruiting participants before payment collection starts.
  const hasTicketCheckout = Boolean(
    event?.is_payable_in_advance || event?.show_pay_button
  );
  const isPlatformRevolutTicket = Boolean(event?.is_platform_event);
  if (ticketingFlowKind === "host_stripe") return true;
  if (ticketingFlowKind === "vaultgrid_revolut") {
    return isPlatformRevolutTicket || hasTicketCheckout;
  }
  if (ticketingFlowKind) return false;

  return isPlatformRevolutTicket || hasTicketCheckout;
}

export function buildEventPaymentSummary(event, t, lang) {
  const pricingModel = String(event?.pricing_model || "").toLowerCase();
  const feeType = Number(event?.fee_type ?? 0);
  const isSharedCost = pricingModel === "shared_cost";
  const isHostPaid = pricingModel === "host_paid";
  const isTicket = isEventTicketPresentation(event);

  if (!isSharedCost && !isHostPaid && feeType !== 1) {
    return null;
  }

  if (isSharedCost && !isTicket) {
    const items = getSharedCostItems(event, t, lang);
    if (items.length === 1) {
      return items[0];
    }
    if (items.length > 1) {
      return {
        label: t("event_description_modal.payment.shared_cost_label", {
          defaultValue: "Shared cost",
        }),
        value: items.map((item) => `${item.label}: ${item.value}`).join(" | "),
        items: items.map((item) => `${item.label}: ${item.value}`),
      };
    }

    const total = getSharedCostTotal(event) || getPrimaryTicketAmount(event);
    if (!total) return null;
    return {
      label: t("event_description_modal.payment.shared_cost_label", {
        defaultValue: "Shared cost",
      }),
      value: t("event_description_modal.payment.amount_plain", {
        value: formatEventMoney(total, event, lang),
        defaultValue: "{{value}}",
      }),
    };
  }

  const segments = [];
  // VaultGrid Revolut events can store the participant price in any shared-cost
  // category. Treat the combined shared cost as the ticket price so the detail
  // row stays consistent with the ticket chip shown above the description.
  const multisportAmount = getPositiveAmount(event?.multisport_ticket_price);
  const regularAmount = isSharedCost
    ? getSharedCostTotal(event) || getPrimaryTicketAmount(event)
    : multisportAmount
    ? getPositiveAmount(event?.payment_category_1) || getPrimaryTicketAmount(event)
    : getPrimaryTicketAmount(event);
  const maleAmount = getPositiveAmount(event?.male_ticket_price);
  const femaleAmount = getPositiveAmount(event?.female_ticket_price);
  const ticketCategories = Array.isArray(event?.ticket_categories)
    ? event.ticket_categories.filter(
        (item) => String(item?.name || "").trim() && getPositiveAmount(item?.price)
      )
    : [];

  if (ticketCategories.length) {
    ticketCategories.forEach((item) => {
      segments.push(`${String(item.name).trim()}: ${formatEventMoney(item.price, event, lang)}`);
    });
  } else if (event?.gender_pricing_enabled && (maleAmount || femaleAmount)) {
    if (maleAmount) {
      segments.push(
        t("event_description_modal.payment.amount_men", {
          value: formatEventMoney(maleAmount, event, lang),
          defaultValue: "{{value}} Men",
        })
      );
    }
    if (femaleAmount) {
      segments.push(
        t("event_description_modal.payment.amount_women", {
          value: formatEventMoney(femaleAmount, event, lang),
          defaultValue: "{{value}} Women",
        })
      );
    }
  } else if (regularAmount) {
    segments.push(
      multisportAmount
        ? t("event_description_modal.payment.amount_regular", {
            value: formatEventMoney(regularAmount, event, lang),
            defaultValue: "{{value}} Regular",
          })
        : t("event_description_modal.payment.amount_plain", {
            value: formatEventMoney(regularAmount, event, lang),
            defaultValue: "{{value}}",
          })
    );
  }

  if (multisportAmount) {
    segments.push(
      t("event_description_modal.payment.amount_multisport", {
        value: formatEventMoney(multisportAmount, event, lang),
        defaultValue: "{{value}} Multisport",
      })
    );
  }

  if (!segments.length) {
    const fallbackAmount = regularAmount || maleAmount || femaleAmount || multisportAmount;
    if (!fallbackAmount) return null;
    segments.push(
      t("event_description_modal.payment.amount_plain", {
        value: formatEventMoney(fallbackAmount, event, lang),
        defaultValue: "{{value}}",
      })
    );
  }

  return {
    label: isTicket
      ? t("event_description_modal.chips.ticket", { defaultValue: "Ticket" })
      : t("event_description_modal.payment.shared_cost_label", {
          defaultValue: "Shared cost",
        }),
    value: segments.join(" | "),
    ...(ticketCategories.length ? { items: segments } : {}),
  };
}
